import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { User, AuditSession, VulnerabilityReport } from '../types/database';

export const useSupabaseAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        if (session?.user) {
          const userData: User = {
            id: session.user.id,
            email: session.user.email || '',
            created_at: session.user.created_at || new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          setUser(userData);
        }
      } catch (err) {
        console.error('Error getting session:', err);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          const userData: User = {
            id: session.user.id,
            email: session.user.email || '',
            created_at: session.user.created_at || new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          setUser(userData);
        } else {
          setUser(null);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  return { user, loading };
};

export const useAuditSessions = (userId?: string) => {
  const [sessions, setSessions] = useState<AuditSession[]>([]);
  const [loading, setLoading] = useState(false);

  const createSession = async (sessionData: Omit<AuditSession, 'id' | 'created_at'>) => {
    if (!supabase) throw new Error('Supabase not connected');
    
    try {
      const { data, error } = await supabase
        .from('audit_sessions')
        .insert([sessionData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error creating session:', err);
      throw err;
    }
  };

  const getSessions = async () => {
    if (!supabase || !userId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('audit_sessions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSessions(data || []);
    } catch (err) {
      console.error('Error fetching sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  return { sessions, loading, createSession, getSessions };
};

export const useVulnerabilityReports = () => {
  const [reports, setReports] = useState<VulnerabilityReport[]>([]);
  const [loading, setLoading] = useState(false);

  const createReport = async (reportData: Omit<VulnerabilityReport, 'id' | 'created_at'>) => {
    if (!supabase) throw new Error('Supabase not connected');
    
    try {
      const { data, error } = await supabase
        .from('vulnerability_reports')
        .insert([reportData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error creating report:', err);
      throw err;
    }
  };

  const getReports = async (sessionId: string) => {
    if (!supabase) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vulnerability_reports')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReports(data || []);
    } catch (err) {
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  return { reports, loading, createReport, getReports };
};